import { AppContext } from '@/contexts/app.context';
import { Grid, Card, CardContent, Typography, Stack, Grow } from '@mui/material';
import { useContext } from 'react';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { ExerciseDTO, DeckDTO } from '@/generated';
import { isDeckEditor } from '@/screens/decks/deck-utils';
import { SimpleExerciseEquipmentList } from '../../equipment/ExerciseEquipmentList';
import { isExerciseEditor } from '../exercise-utils';
import { SimpleExercisePrerequisiteList, SimpleProgressionRegressionList } from '@/screens/exercises/elements/ExerciseProgressionList';
import { StarRating } from '@/components/StarRating';
import { NavigateToExerciseDetailsButton } from '@/components/icon-buttons/NavigateToExerciseDetailsButton';
import { EditExerciseButton } from '@/components/icon-buttons/EditExerciseButton';
import { DeleteExerciseButton } from '@/components/icon-buttons/DeleteExerciseButton';
import { RemoveExerciseButton } from '@/components/icon-buttons/RemoveExerciseButton';
import { CopyExerciseLinkButton } from '@/components/icon-buttons/CopyExerciseLinkButton';
import { useBodyZoneListQuery } from '@/hooks/query-hooks';
import { ExerciseMetrics } from './ExerciseMetrics';

interface CardExerciseProps {
  exercise: ExerciseDTO;
  timeout: number;
  deck?: DeckDTO;
  onClick?: () => void;
  onExerciseDeleted?: () => void;
}

export const ExerciseCardGridElement = (props: CardExerciseProps & { setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>> }) => {
  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <ExerciseCard {...props} onClick={props.onClick} />
    </Grid>
  );
};

export const ExerciseCard = (props: CardExerciseProps & { setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const { exercise, deck, setShowDeleteModal } = props;
  const { bodyZoneList } = useBodyZoneListQuery();
  const context = useContext(AppContext);
  const user = context.state?.userProfile;

  return (
    <Grow in={true} timeout={props.timeout}>
      <Card
        onClick={props.onClick}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: user && isDeckEditor(props.deck, user) ? 'pointer' : 'auto' }}
        className="h-100"
      >
        <CardContent sx={{ flex: 1 }}>
          <Stack sx={{ height: '100%', gap: 1 }}>
            <Stack sx={{ flexDirection: 'row', gap: 2 }}>
              <ImageFromServer sx={{ width: 150, height: 150 }} thumbnail={true} alt="" id={exercise.imageId} />
              <Stack sx={{ flex: 1 }} direction={'column'}>
                <Stack sx={{ alignContent: 'center', flexDirection: 'row' }}>
                  <Stack sx={{ flex: 1 }}>
                    <Typography
                      sx={{ overflowWrap: 'break-word', flex: 1, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      variant="h6"
                    >
                      {props.exercise.name}
                    </Typography>
                    <Typography variant="body2">
                      {bodyZoneList?.find((bz) => bz.id === props.exercise.mainBodyZoneId)?.name ?? 'N.A.'}
                      {props.exercise.isUniLateral ? ', Unilateral' : ''}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <StarRating rating={exercise.difficulty} />
                  <ExerciseMetrics exercise={exercise} />
                </Stack>
              </Stack>
            </Stack>

            <Typography sx={{ my: 1, minHeight: 100, display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} variant="body2">
              {exercise?.description || 'No Description'}
            </Typography>

            <Grid container spacing={1}>
              <Grid size={6}>
                <Stack spacing={0.5}>
                  <SimpleExerciseEquipmentList equipmentConfigs={props.exercise.equipment} />
                  <SimpleProgressionRegressionList title="Progressions" ids={props.exercise.progressionExerciseIds} />
                  <SimpleProgressionRegressionList title="Regressions" ids={props.exercise.regressionExerciseIds} />
                  <SimpleProgressionRegressionList
                    title="Support"
                    ids={props.exercise.supportExercises.map((supportExercise) => supportExercise.exerciseId)}
                    descriptions={Object.fromEntries(props.exercise.supportExercises.map((supportExercise) => [supportExercise.exerciseId, supportExercise.description]))}
                  />
                </Stack>
              </Grid>
              <Grid size={6}>
                <SimpleExercisePrerequisiteList prerequisites={props.exercise.prerequisites} />
              </Grid>
            </Grid>

            <Stack sx={{ mt: 'auto', flexDirection: 'row' }}>
              {deck !== undefined && <NavigateToExerciseDetailsButton exerciseId={exercise.id} />}
              <CopyExerciseLinkButton exerciseId={exercise.id} />
              {user && isDeckEditor(deck, user) && <RemoveExerciseButton setShowDeleteModal={setShowDeleteModal} />}
              {user && !deck && isExerciseEditor(exercise, user) && <EditExerciseButton exerciseId={exercise.id} />}
              {user && !deck && isExerciseEditor(exercise, user) && <DeleteExerciseButton setShowDeleteModal={setShowDeleteModal} />}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Grow>
  );
};
